import React, { useState, useRef, useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';

// Breakpoints for responsive images
const BREAKPOINTS = [640, 768, 1024, 1280, 1920] as const;

// Check format support (cached at module level for performance)
let formatSupport: { webp: boolean; avif: boolean } | null = null;

const detectFormatSupport = (): { webp: boolean; avif: boolean } => {
  if (formatSupport) return formatSupport;
  
  if (typeof window === 'undefined') {
    formatSupport = { webp: false, avif: false };
    return formatSupport;
  }

  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;

  formatSupport = {
    avif: canvas.toDataURL('image/avif').startsWith('data:image/avif'),
    webp: canvas.toDataURL('image/webp').startsWith('data:image/webp')
  };

  return formatSupport;
};

// Generate optimized URL for Unsplash images
const generateOptimizedUrl = (
  src: string,
  width: number,
  format?: 'webp' | 'avif' | 'jpg'
): string => {
  // Handle Unsplash URLs
  if (src.includes('unsplash.com')) {
    try {
      const url = new URL(src);
      url.searchParams.set('w', width.toString());
      url.searchParams.set('q', '80');
      url.searchParams.set('fit', 'crop');
      url.searchParams.set('auto', 'format');
      if (format && format !== 'jpg') {
        url.searchParams.set('fm', format);
      }
      return url.toString();
    } catch {
      return src;
    }
  }
  
  // For local/other images, return as-is (they should be pre-optimized)
  return src;
};

// Generate srcSet for responsive images
const generateSrcSet = (src: string, format?: 'webp' | 'avif' | 'jpg'): string => {
  if (!src.includes('unsplash.com')) return '';
  
  return BREAKPOINTS
    .map(bp => `${generateOptimizedUrl(src, bp, format)} ${bp}w`)
    .join(', ');
};

interface OptimizedImageProps {
  /** Image source URL */
  src: string;
  /** Alt text for accessibility */
  alt: string;
  /** Additional CSS classes */
  className?: string;
  /** Explicit width for layout stability */
  width?: number | string;
  /** Explicit height for layout stability */
  height?: number | string;
  /** Aspect ratio (e.g., "16/9", "4/3") - alternative to height */
  aspectRatio?: string;
  /** Load immediately (for hero/above-fold images) */
  priority?: boolean;
  /** Responsive sizes attribute */
  sizes?: string;
  /** Object fit behavior */
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  /** Object position */
  objectPosition?: string;
  /** Callback when image loads */
  onLoad?: () => void;
  /** Callback on error */
  onError?: () => void;
  /** Container className (wrapping div) */
  containerClassName?: string;
  /** Fill container (absolute positioning) */
  fill?: boolean;
  /** Placeholder color/image */
  placeholder?: string;
  /** Enable blur-up effect */
  blurUp?: boolean;
}

const DEFAULT_PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PC9zdmc+';

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className,
  width,
  height,
  aspectRatio,
  priority = false,
  sizes = '100vw',
  objectFit = 'cover',
  objectPosition = 'center',
  onLoad,
  onError,
  containerClassName,
  fill = false,
  placeholder = DEFAULT_PLACEHOLDER,
  blurUp = true
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isErrored, setIsErrored] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(priority);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Format detection
  const formatSupportResult = useMemo(() => detectFormatSupport(), []);
  const preferredFormat = formatSupportResult.avif ? 'avif' : formatSupportResult.webp ? 'webp' : 'jpg';

  // Generate srcSet
  const srcSet = useMemo(() => generateSrcSet(src, preferredFormat), [src, preferredFormat]);
  
  // Generate optimized src (fallback for browsers without srcset support)
  const optimizedSrc = useMemo(() => {
    const targetWidth = typeof width === 'number' ? width : 1920;
    return generateOptimizedUrl(src, targetWidth, preferredFormat);
  }, [src, width, preferredFormat]);

  // Preload priority images
  useEffect(() => {
    if (!priority) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = optimizedSrc;
    link.fetchPriority = 'high';
    
    if (srcSet) {
      link.imageSrcset = srcSet;
      link.imageSizes = sizes;
    }
    
    document.head.appendChild(link);

    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, [priority, optimizedSrc, srcSet, sizes]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || shouldLoad) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // Start loading 200px before entering viewport
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [priority, shouldLoad]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    console.warn('OptimizedImage: Failed to load:', src);
    setIsErrored(true);
    onError?.();
  };

  // Determine actual image source
  const imageSrc = isErrored ? placeholder : (shouldLoad ? optimizedSrc : placeholder);
  const priorityAttribute = priority
    ? ({ fetchpriority: 'high' } as React.ImgHTMLAttributes<HTMLImageElement>)
    : {};

  // Container styles for layout stability
  const containerStyles: React.CSSProperties = {};
  
  if (fill) {
    // Fill mode: absolute positioning
  } else {
    if (width) containerStyles.width = typeof width === 'number' ? `${width}px` : width;
    if (height) containerStyles.height = typeof height === 'number' ? `${height}px` : height;
    if (aspectRatio) containerStyles.aspectRatio = aspectRatio;
  }

  return (
    <div 
      ref={containerRef}
      className={cn(
        "relative overflow-hidden",
        fill && "absolute inset-0",
        containerClassName
      )}
      style={containerStyles}
    >
      {/* Placeholder background for layout stability */}
      {blurUp && !isLoaded && (
        <div 
          className="absolute inset-0 bg-muted animate-pulse"
          aria-hidden="true"
        />
      )}
      
      {/* Picture element for format switching */}
      <picture>
        {/* AVIF source (best compression) */}
        {srcSet && formatSupportResult.avif && (
          <source
            type="image/avif"
            srcSet={generateSrcSet(src, 'avif')}
            sizes={sizes}
          />
        )}
        
        {/* WebP source (good compression, wide support) */}
        {srcSet && formatSupportResult.webp && (
          <source
            type="image/webp"
            srcSet={generateSrcSet(src, 'webp')}
            sizes={sizes}
          />
        )}
        
        {/* Fallback img with JPEG srcSet */}
        <img
          ref={imageRef}
          src={imageSrc}
          srcSet={shouldLoad && srcSet ? srcSet : undefined}
          sizes={shouldLoad && srcSet ? sizes : undefined}
          alt={alt}
          width={typeof width === 'number' ? width : undefined}
          height={typeof height === 'number' ? height : undefined}
          loading={priority ? 'eager' : 'lazy'}
          decoding={priority ? 'sync' : 'async'}
          {...priorityAttribute}
          onLoad={handleLoad}
          onError={handleError}
          className={cn(
            "w-full h-full",
            `object-${objectFit}`,
            blurUp && !isLoaded && "opacity-0",
            blurUp && isLoaded && "opacity-100 transition-opacity duration-300",
            className
          )}
          style={{ objectPosition }}
        />
      </picture>
    </div>
  );
};

// Hero-specific wrapper with preloading and high priority
interface HeroImageProps extends Omit<OptimizedImageProps, 'priority' | 'fill'> {
  /** Overlay gradient class */
  overlayClassName?: string;
  /** Image filter values */
  brightness?: number;
  contrast?: number;
  saturate?: number;
}

export const HeroImage: React.FC<HeroImageProps> = ({
  overlayClassName = "absolute inset-0 bg-gradient-to-br from-black/40 via-black/30 to-black/50",
  brightness = 1,
  contrast = 1,
  saturate = 1,
  className,
  ...props
}) => {
  const filterStyle = brightness !== 1 || contrast !== 1 || saturate !== 1
    ? { filter: `brightness(${brightness}) contrast(${contrast}) saturate(${saturate})` }
    : undefined;

  return (
    <div className="absolute inset-0 z-0">
      <OptimizedImage
        {...props}
        width={props.width ?? 1920}
        height={props.height ?? 1080}
        priority
        fill
        sizes="100vw"
        className={cn(className)}
        containerClassName="absolute inset-0"
        blurUp={false} // Heroes should load immediately
      />
      {/* Apply filter as overlay to avoid re-render */}
      {filterStyle && (
        <div className="absolute inset-0" style={filterStyle} aria-hidden="true" />
      )}
      {overlayClassName && (
        <div className={overlayClassName} aria-hidden="true" />
      )}
    </div>
  );
};

// Background image wrapper for cards/sections
interface BackgroundImageProps extends Omit<OptimizedImageProps, 'fill'> {
  /** Content to render over the image */
  children?: React.ReactNode;
  /** Overlay gradient/color */
  overlay?: string;
}

export const BackgroundImage: React.FC<BackgroundImageProps> = ({
  children,
  overlay,
  containerClassName,
  ...props
}) => {
  return (
    <div className={cn("relative overflow-hidden", containerClassName)}>
      <OptimizedImage
        {...props}
        fill
        containerClassName="absolute inset-0"
      />
      {overlay && (
        <div className={cn("absolute inset-0", overlay)} aria-hidden="true" />
      )}
      {children && (
        <div className="relative z-10">
          {children}
        </div>
      )}
    </div>
  );
};

export default OptimizedImage;
